import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ThumbnailUploader } from "@/components/ThumbnailUploader";
import { TradingChartLoader } from "@/components/TradingChartLoader";
import { Save, ExternalLink, Eye, ImageIcon, Type, LayoutGrid, GitBranch, Megaphone, FileText } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const ICON_OPTIONS = [
  { value: "shield", label: "Shield" },
  { value: "graduation", label: "Graduation Cap" },
  { value: "sparkles", label: "Sparkles" },
  { value: "video", label: "Video" },
  { value: "book", label: "Book" },
  { value: "file", label: "File" },
  { value: "calendar", label: "Calendar" },
  { value: "trending", label: "Trending Up" },
];

function CardEditor({ title, items = [], onChange, fields = [] }) {
  // fields: [{ key, label, type: "text"|"textarea"|"icon" }]
  const setItem = (idx, k, v) => {
    const next = [...items];
    next[idx] = { ...next[idx], [k]: v };
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <h3 className="font-heading font-semibold flex items-center gap-2">
        <LayoutGrid className="h-4 w-4 text-primary" /> {title}
      </h3>
      {items.map((item, idx) => (
        <Card key={idx} className="glass-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Card #{idx + 1}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" || f.fullWidth ? "sm:col-span-2" : ""}>
                  <Label className="text-xs">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      rows={2}
                      value={item[f.key] || ""}
                      onChange={(e) => setItem(idx, f.key, e.target.value)}
                      data-testid={`card-${title}-${idx}-${f.key}`}
                    />
                  ) : f.type === "icon" ? (
                    <select
                      className="flex h-10 w-full rounded-lg border border-border bg-card/40 px-3 text-sm focus:outline-none focus:border-primary/60"
                      value={item[f.key] || ""}
                      onChange={(e) => setItem(idx, f.key, e.target.value)}
                    >
                      <option value="">— Select icon —</option>
                      {ICON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <Input
                      value={item[f.key] || ""}
                      onChange={(e) => setItem(idx, f.key, e.target.value)}
                      data-testid={`card-${title}-${idx}-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function MetricsEditor({ items = [], onChange }) {
  const setItem = (idx, k, v) => {
    const next = [...items];
    next[idx] = { ...next[idx], [k]: v };
    onChange(next);
  };
  return (
    <div>
      <h4 className="text-sm font-semibold mb-2">Hero Metrics</h4>
      <div className="grid sm:grid-cols-3 gap-3">
        {items.map((m, idx) => (
          <Card key={idx} className="glass-card">
            <CardContent className="p-3 space-y-2">
              <Label className="text-xs">Value</Label>
              <Input value={m.value || ""} onChange={(e) => setItem(idx, "value", e.target.value)} data-testid={`metric-${idx}-value`} />
              <Label className="text-xs">Label</Label>
              <Input value={m.label || ""} onChange={(e) => setItem(idx, "label", e.target.value)} data-testid={`metric-${idx}-label`} />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/content/homepage");
      // Ensure default structures exist
      setContent({
        logo_url: "",
        hero: { metrics: [], ...data.hero },
        trust_points: [],
        features_section: {},
        features: [],
        steps_section: {},
        steps: [],
        final_cta: {},
        footer: {},
        ...data,
      });
    } catch {
      toast.error("Failed to load content");
    }
  };

  useEffect(() => { load(); }, []);

  const setSection = (key, value) => {
    setContent((c) => ({ ...c, [key]: value }));
  };
  const setNested = (section, key, value) => {
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/content/homepage", content);
      toast.success("Content saved and published to the website!");
    } catch {
      toast.error("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <AdminLayout title="Content"><div className="flex items-center justify-center py-16"><TradingChartLoader size="lg" text="Loading content..." /></div></AdminLayout>;

  const hero = content.hero || {};
  const featuresSec = content.features_section || {};
  const stepsSec = content.steps_section || {};
  const finalCta = content.final_cta || {};
  const footer = content.footer || {};

  return (
    <AdminLayout title="Content Manager">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Type className="h-7 w-7 text-primary" /> Content Manager
          </h1>
          <p className="mt-1 text-muted-foreground max-w-2xl">
            Edit text and upload images for the homepage (eLearningsaham.my). Changes go live immediately after you click <span className="font-semibold text-foreground">Save & Publish</span>.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild variant="outline" data-testid="preview-site-btn">
            <a href="/" target="_blank" rel="noreferrer"><Eye className="h-4 w-4" /> View Website <ExternalLink className="h-3.5 w-3.5" /></a>
          </Button>
          <Button onClick={save} disabled={saving} className="btn-glow" data-testid="cms-save-btn">
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save & Publish"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="branding">
        <TabsList>
          <TabsTrigger value="branding"><ImageIcon className="h-3.5 w-3.5" /> Logo</TabsTrigger>
          <TabsTrigger value="hero"><Megaphone className="h-3.5 w-3.5" /> Hero</TabsTrigger>
          <TabsTrigger value="trust"><LayoutGrid className="h-3.5 w-3.5" /> Trust</TabsTrigger>
          <TabsTrigger value="features"><GitBranch className="h-3.5 w-3.5" /> Features</TabsTrigger>
          <TabsTrigger value="steps"><FileText className="h-3.5 w-3.5" /> Steps</TabsTrigger>
          <TabsTrigger value="cta"><Megaphone className="h-3.5 w-3.5" /> Final CTA</TabsTrigger>
          <TabsTrigger value="footer"><FileText className="h-3.5 w-3.5" /> Footer</TabsTrigger>
        </TabsList>

        {/* BRANDING / LOGO */}
        <TabsContent value="branding" className="mt-6">
          <Card className="glass-card max-w-2xl">
            <CardContent className="p-6 space-y-4">
              <h3 className="font-heading font-semibold flex items-center gap-2"><ImageIcon className="h-4 w-4 text-primary" /> Logo</h3>
              <p className="text-sm text-muted-foreground">This logo appears in the header, sidebar, sign-in, and footer. Use a square, transparent logo.</p>
              <ThumbnailUploader
                value={content.logo_url}
                onChange={(v) => setSection("logo_url", v)}
                kind="logo"
                label="Logo Maahad Bursa"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                testId="cms-logo-uploader"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* HERO */}
        <TabsContent value="hero" className="mt-6 space-y-4">
          <Card className="glass-card">
            <CardContent className="p-6 space-y-4">
              <h3 className="font-heading font-semibold flex items-center gap-2"><Megaphone className="h-4 w-4 text-primary" /> Hero Section</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Badge Text</Label>
                  <Input value={hero.badge_text || ""} onChange={(e) => setNested("hero", "badge_text", e.target.value)} data-testid="hero-badge-input" />
                </div>
                <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Headline Line 1</Label>
                    <Input value={hero.headline_line1 || ""} onChange={(e) => setNested("hero", "headline_line1", e.target.value)} data-testid="hero-h1" />
                  </div>
                  <div>
                    <Label>Headline Line 2 (gradient)</Label>
                    <Input value={hero.headline_line2 || ""} onChange={(e) => setNested("hero", "headline_line2", e.target.value)} data-testid="hero-h2" />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label>Subheadline</Label>
                  <Textarea rows={3} value={hero.subheadline || ""} onChange={(e) => setNested("hero", "subheadline", e.target.value)} data-testid="hero-sub" />
                </div>
                <div>
                  <Label>Primary CTA Text</Label>
                  <Input value={hero.primary_cta || ""} onChange={(e) => setNested("hero", "primary_cta", e.target.value)} />
                </div>
                <div>
                  <Label>Secondary CTA Text</Label>
                  <Input value={hero.secondary_cta || ""} onChange={(e) => setNested("hero", "secondary_cta", e.target.value)} />
                </div>
              </div>
              <MetricsEditor items={hero.metrics || []} onChange={(arr) => setNested("hero", "metrics", arr)} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* TRUST POINTS */}
        <TabsContent value="trust" className="mt-6">
          <Card className="glass-card">
            <CardContent className="p-6">
              <CardEditor
                title="Trust Points (3 cards)"
                items={content.trust_points || []}
                onChange={(arr) => setSection("trust_points", arr)}
                fields={[
                  { key: "icon", label: "Ikon", type: "icon" },
                  { key: "title", label: "Title" },
                  { key: "desc", label: "Description", type: "textarea" },
                ]}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* FEATURES */}
        <TabsContent value="features" className="mt-6 space-y-4">
          <Card className="glass-card">
            <CardContent className="p-6 space-y-3">
              <h3 className="font-heading font-semibold">Features Section Header</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Kicker</Label>
                  <Input value={featuresSec.kicker || ""} onChange={(e) => setNested("features_section", "kicker", e.target.value)} />
                </div>
                <div>
                  <Label>Heading</Label>
                  <Input value={featuresSec.heading || ""} onChange={(e) => setNested("features_section", "heading", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Description</Label>
                  <Textarea rows={2} value={featuresSec.description || ""} onChange={(e) => setNested("features_section", "description", e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <CardEditor
                title="Feature Cards (5 cards)"
                items={content.features || []}
                onChange={(arr) => setSection("features", arr)}
                fields={[
                  { key: "icon", label: "Ikon", type: "icon" },
                  { key: "title", label: "Title" },
                  { key: "desc", label: "Description", type: "textarea" },
                ]}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* STEPS */}
        <TabsContent value="steps" className="mt-6 space-y-4">
          <Card className="glass-card">
            <CardContent className="p-6 space-y-3">
              <h3 className="font-heading font-semibold">Steps Section Header</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Kicker</Label>
                  <Input value={stepsSec.kicker || ""} onChange={(e) => setNested("steps_section", "kicker", e.target.value)} />
                </div>
                <div>
                  <Label>Heading</Label>
                  <Input value={stepsSec.heading || ""} onChange={(e) => setNested("steps_section", "heading", e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <CardEditor
                title="Steps (3 cards)"
                items={content.steps || []}
                onChange={(arr) => setSection("steps", arr)}
                fields={[
                  { key: "n", label: "Number (e.g. 01)" },
                  { key: "title", label: "Title" },
                  { key: "desc", label: "Description", type: "textarea" },
                ]}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* FINAL CTA */}
        <TabsContent value="cta" className="mt-6">
          <Card className="glass-card max-w-3xl">
            <CardContent className="p-6 space-y-3">
              <h3 className="font-heading font-semibold flex items-center gap-2"><Megaphone className="h-4 w-4 text-primary" /> Final CTA Section</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Tagline (badge atas)</Label>
                  <Input value={finalCta.tagline || ""} onChange={(e) => setNested("final_cta", "tagline", e.target.value)} />
                </div>
                <div>
                  <Label>Headline Part 1</Label>
                  <Input value={finalCta.headline_part1 || ""} onChange={(e) => setNested("final_cta", "headline_part1", e.target.value)} />
                </div>
                <div>
                  <Label>Headline Part 2 (gradient)</Label>
                  <Input value={finalCta.headline_part2 || ""} onChange={(e) => setNested("final_cta", "headline_part2", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Description</Label>
                  <Textarea rows={2} value={finalCta.description || ""} onChange={(e) => setNested("final_cta", "description", e.target.value)} />
                </div>
                <div>
                  <Label>Button Text</Label>
                  <Input value={finalCta.button || ""} onChange={(e) => setNested("final_cta", "button", e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FOOTER */}
        <TabsContent value="footer" className="mt-6">
          <Card className="glass-card max-w-3xl">
            <CardContent className="p-6 space-y-3">
              <h3 className="font-heading font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Footer</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Brand Name</Label>
                  <Input value={footer.brand_name || ""} onChange={(e) => setNested("footer", "brand_name", e.target.value)} />
                </div>
                <div>
                  <Label>Brand Subtitle</Label>
                  <Input value={footer.brand_subtitle || ""} onChange={(e) => setNested("footer", "brand_subtitle", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Brand Description</Label>
                  <Textarea rows={2} value={footer.description || ""} onChange={(e) => setNested("footer", "description", e.target.value)} />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input type="email" value={footer.contact_email || ""} onChange={(e) => setNested("footer", "contact_email", e.target.value)} />
                </div>
                <div>
                  <Label>Domain</Label>
                  <Input value={footer.domain || ""} onChange={(e) => setNested("footer", "domain", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Disclaimer</Label>
                  <Textarea rows={3} value={footer.disclaimer || ""} onChange={(e) => setNested("footer", "disclaimer", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Copyright text</Label>
                  <Input value={footer.copyright || ""} onChange={(e) => setNested("footer", "copyright", e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sticky save bar at bottom */}
      <div className="mt-8 flex justify-end gap-2 sticky bottom-4">
        <Button asChild variant="outline">
          <a href="/" target="_blank" rel="noreferrer"><Eye className="h-4 w-4" /> Preview</a>
        </Button>
        <Button onClick={save} disabled={saving} className="btn-glow" data-testid="cms-save-bottom">
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save & Publish"}
        </Button>
      </div>
    </AdminLayout>
  );
}
